import { useCallback, useRef, useState } from "react";
import { confirmLoginEnrollment, restartLoginEnrollment } from "../../../services/mfa/mfaApi";
import { describeMfaError } from "../../../services/mfa/mfaErrorMessages";
import { normaliseCode } from "./mfaCodeFormat";

const INVALID_FORMAT = "Enter the 6-digit code.";
const NOT_SETUP = { setup: false, stepLabel: null, skipMobileStep: false };

// SSN/TIN fallback (docs/specs/authenticator-mfa-ssn-fallback.md): after a right TIN the login screen binds an
// authenticator, and only the confirmed code opens the session. Lives beside useMfaLogin, which owns the tx
// (`txRef`), the busy guard (`run`) and the stage. Everything here is memory only, like the tx.
// Login setup (docs/specs/login-setup.md §2) opens the same dialog with options `{ setup, stepLabel, skipMobileStep }`:
// an expired QR then gets a fresh one from enroll-start, never the code step (whose next verify is SETUP_REQUIRED).
const useLoginEnrollment = ({ run, txRef, setStage, onSession }) => {
  const [enrollment, setEnrollment] = useState(null);
  const [enrollError, setEnrollError] = useState(null);
  const [mobileToken, setMobileToken] = useState(null);
  const [options, setOptions] = useState(NOT_SETUP);
  const sessionRef = useRef(null);
  const optionsRef = useRef(NOT_SETUP);

  const beginEnrollment = useCallback((res, next = NOT_SETUP) => {
    optionsRef.current = next;
    setOptions(next);
    setEnrollError(null);
    setEnrollment(res);
    setStage("enroll");
  }, [setStage]);

  // Setup only; the caller holds the busy guard. In setup the dialog is all that is on screen, so a failure that does
  // not end the sign-in is shown in it (build review, lane 11); a restart code still leaves through `run`.
  const renewEnrollment = useCallback(async () => {
    try {
      beginEnrollment(await restartLoginEnrollment({ mfaTx: txRef.current }), optionsRef.current);
    } catch (e) {
      const d = describeMfaError(e);
      if (d.restart) throw e;
      setEnrollError(d.message);
    }
  }, [beginEnrollment, txRef]);

  const cancelEnrollment = useCallback(() => {
    setEnrollment(null);
    setEnrollError(null);
    setStage("mfa");
  }, [setStage]);

  const handOver = useCallback(async () => {
    const session = sessionRef.current;
    sessionRef.current = null;
    setMobileToken(null);
    await onSession(session);
  }, [onSession]);

  // Errors here belong to the setup dialog, except an expired setup or sign-in, which leave it.
  // A confirmed code hands the session over at once — no recovery codes since docs/specs/mfa-ux-cleanup.md §1 —
  // unless Oracle has no mobile for this user: then the "add a mobile" step comes first and hands it over itself
  // (Skip, Save or failure).
  const confirmEnrollment = useCallback(
    (raw) =>
      run(async () => {
        const code = normaliseCode("totp", raw);
        if (!code) return setEnrollError(INVALID_FORMAT);
        setEnrollError(null);
        try {
          const res = await confirmLoginEnrollment({ mfaTx: txRef.current, enrollmentId: enrollment?.enrollment_id, code });
          if (!res?.session) throw new Error("enroll-confirm returned no session");
          sessionRef.current = res.session;
          setEnrollment(null);
          if (res.sms_alerts === false && !optionsRef.current.skipMobileStep) { // docs/specs/authenticator-mfa-sms-enroll.md
            setMobileToken(res.session.token ?? null);
            setStage("mobile");
            return;
          }
        } catch (e) {
          const d = describeMfaError(e);
          if (d.code === "ENROLLMENT_EXPIRED") {
            if (optionsRef.current.setup) return renewEnrollment();
            cancelEnrollment();
            throw e;
          }
          if (d.restart) throw e;
          setEnrollError(d.message);
          return;
        }
        await handOver();
      }),
    [run, txRef, enrollment, setStage, cancelEnrollment, renewEnrollment, handOver],
  );

  const finishMobileStep = useCallback(() => run(handOver), [run, handOver]);
  const newQr = useCallback(() => run(renewEnrollment), [run, renewEnrollment]);

  const resetEnrollment = useCallback(() => {
    setEnrollment(null);
    setEnrollError(null);
    setMobileToken(null);
    sessionRef.current = null;
    optionsRef.current = NOT_SETUP;
    setOptions(NOT_SETUP);
  }, []);

  return {
    enrollment, enrollError, mobileToken, enrollStepLabel: options.stepLabel, inSetup: options.setup,
    beginEnrollment, confirmEnrollment, cancelEnrollment, finishMobileStep, resetEnrollment, newQr,
  };
};

export default useLoginEnrollment;
